import countBy from "lodash/countBy";
import { DiffType, computeLineInformation } from "react-diff-viewer/lib/compute-lines";
import { stringify } from "yaml";

import { fromJSON } from "~/tools/from-json";
import { isArray } from "~/tools/is-array";
import { isPlainObject } from "~/tools/is-plain-object";
import { isString } from "~/tools/is-string";
import { mapValues } from "~/tools/map-values";
import type { Record } from "~/tools/record";
import { toJSON } from "~/tools/to-json";

/**
 * The detects JSON strings and formats them recursively.
 * ! It prettifies only the first level of JSON strings.
 */
function prettifyJSON(input: unknown): unknown {
	if (isArray(input)) {
		// prettify every field of an array
		return input.map(prettifyJSON);
	}

	if (isPlainObject(input)) {
		// prettify every field of an object
		return mapValues(input, prettifyJSON);
	}

	// JSON must be a string
	if (!isString(input)) {
		return input;
	}

	const [error, output] = fromJSON(input);

	// parsing failed - return original input
	if (error != null) {
		return input;
	}

	// convert the value back to JSON string but with indentation
	return toJSON(output.unsafe, 2);
}

interface JsonToYamlOptions {
	/**
	 * Sort the object fields alphabetically when true.
	 *
	 * @default true
	 */
	sort?: boolean;
}

/**
 * Convert a JSON object to a YAML string.
 */
export const jsonToYaml = (input: unknown, { sort = true }: JsonToYamlOptions = {}): string => {
	if (sort) {
		// run through the json conversion so the object's fields are sorted
		const [, output] = fromJSON(toJSON(input));

		return stringify(prettifyJSON(output?.unsafe), { lineWidth: 0 }) ?? "";
	}

	return stringify(prettifyJSON(input), { lineWidth: 0 }) ?? "";
};

export interface LinesDiffCount {
	added: number;
	removed: number;
}

export const countYamlLinesDiff = (first: unknown, second: unknown): LinesDiffCount => {
	const { lineInformation } = computeLineInformation(
		first == null ? "" : jsonToYaml(first),
		second == null ? "" : jsonToYaml(second),
	);
	const counts: Record<DiffType, number, "readonly", "optional"> = countBy(
		lineInformation.flatMap(({ left, right }) => [left, right]),
		"type",
	);

	return {
		added: counts[DiffType.ADDED] ?? 0,
		removed: counts[DiffType.REMOVED] ?? 0,
	};
};
